import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { Badge } from '@/components/ui/badge';
import { MemberPhoto } from '@/components/ui/member-photo';
import { cn } from '@/utils/cn';

import {
  buildFamilyTreeLayout,
  buildHierarchicalListRows,
  type DescendantBranch,
} from './family-tree-layout';
import {
  displayPersonName,
  type FamilyLink,
  type FamilyPerson,
  type FamilyTreeData,
  type FamilyView,
} from './family-tree-types';
import { formatFamilyRelationship } from './member-relationship-label';

interface FamilyTreeDisplayProps {
  centerPerson: FamilyPerson;
  tree: FamilyTreeData;
  view: FamilyView;
}

const formatLinkRelationship = (
  link: FamilyLink,
  t: ReturnType<typeof useTranslation<'people'>>['t'],
) => formatFamilyRelationship(t, link.relationship, link.related.gender);

const PersonCard = ({
  person,
  relationshipLabel,
  highlight = false,
  className,
}: {
  person: FamilyPerson;
  relationshipLabel?: string;
  highlight?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation('people');
  const name = displayPersonName(person);

  return (
    <div
      className={cn(
        'flex w-[7.5rem] shrink-0 flex-col items-center gap-2 rounded-lg border border-border bg-surface px-2 py-3 text-center',
        highlight && 'border-brand bg-brand/5 shadow-sm',
        className,
      )}
    >
      <MemberPhoto src={person.photoUrl} name={name} size="md" />
      <div className="min-w-0 space-y-1">
        <MemberNameLink
          memberId={person.id}
          name={name}
          className="justify-center text-center font-medium"
        />
        {relationshipLabel ? (
          <p className="text-caption text-fg-secondary">{relationshipLabel}</p>
        ) : null}
        {person.status !== 'active' ? (
          <Badge variant="neutral" className="mx-auto">
            {t(`members.status.${person.status}`)}
          </Badge>
        ) : null}
      </div>
    </div>
  );
};

const TreeConnectorDown = () => (
  <div className="flex flex-col items-center">
    <div className="h-4 w-px bg-border" />
  </div>
);

const TreeConnectorFork = ({ count }: { count: number }) => {
  if (count <= 1) return <TreeConnectorDown />;

  return (
    <div className="flex w-full flex-col items-center">
      <div className="h-4 w-px bg-border" />
      <div className="relative h-px w-full max-w-full bg-border">
        <div
          className="absolute top-0 h-px bg-border"
          style={{
            left: `${100 / (count * 2)}%`,
            right: `${100 / (count * 2)}%`,
          }}
        />
      </div>
    </div>
  );
};

const DescendantColumn = ({
  branch,
  t,
}: {
  branch: DescendantBranch;
  t: ReturnType<typeof useTranslation<'people'>>['t'];
}) => (
  <div className="flex flex-col items-center gap-2">
    <PersonCard
      person={branch.link.related}
      relationshipLabel={formatLinkRelationship(branch.link, t)}
    />
    {branch.descendants.length > 0 ? (
      <>
        <TreeConnectorDown />
        <div className="flex items-start justify-center gap-4">
          {branch.descendants.map((child) => (
            <DescendantColumn key={child.link.id} branch={child} t={t} />
          ))}
        </div>
      </>
    ) : null}
  </div>
);

const FamilyTreeView = ({
  centerPerson,
  tree,
}: {
  centerPerson: FamilyPerson;
  tree: FamilyTreeData;
}) => {
  const { t } = useTranslation('people');
  const layout = buildFamilyTreeLayout(centerPerson, tree);

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface-sunken/40 p-6">
      <div className="inline-flex min-w-full flex-col gap-8">
        {layout.parentBranches.length > 0 ? (
          <div className="flex flex-wrap items-end justify-center gap-8">
            {layout.parentBranches.map((branch) => (
              <div key={branch.parent.id} className="flex flex-col items-center gap-2">
                {branch.grandparents.length > 0 ? (
                  <>
                    <div className="flex flex-wrap items-end justify-center gap-3">
                      {branch.grandparents.map((link) => (
                        <PersonCard
                          key={link.id}
                          person={link.related}
                          relationshipLabel={formatLinkRelationship(link, t)}
                        />
                      ))}
                    </div>
                    <TreeConnectorDown />
                  </>
                ) : null}

                <div className="flex flex-wrap items-start justify-center gap-3">
                  <PersonCard
                    person={branch.parent.related}
                    relationshipLabel={formatLinkRelationship(branch.parent, t)}
                  />
                  {branch.uncles.map(({ link, cousins }) => (
                    <div key={link.id} className="flex flex-col items-center gap-2">
                      <PersonCard
                        person={link.related}
                        relationshipLabel={formatLinkRelationship(link, t)}
                      />
                      {cousins.length > 0 ? (
                        <>
                          <TreeConnectorDown />
                          <div className="flex items-start gap-3">
                            {cousins.map((cousin) => (
                              <DescendantColumn key={cousin.link.id} branch={cousin} t={t} />
                            ))}
                          </div>
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
                <TreeConnectorDown />
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-start justify-center gap-6">
          {layout.siblingBranches.map((branch) => (
            <div key={branch.sibling.id} className="flex flex-col items-center gap-2">
              <PersonCard
                person={branch.sibling.related}
                relationshipLabel={formatLinkRelationship(branch.sibling, t)}
              />
              {branch.descendants.length > 0 ? (
                <>
                  <TreeConnectorFork count={branch.descendants.length} />
                  <div className="flex items-start gap-4">
                    {branch.descendants.map((descendant) => (
                      <DescendantColumn key={descendant.link.id} branch={descendant} t={t} />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ))}

          <div className="flex flex-col items-center gap-2">
            <PersonCard
              person={centerPerson}
              highlight
              relationshipLabel={t('families.tree.selectedPerson')}
            />
            {layout.centerDescendants.length > 0 ? (
              <>
                <TreeConnectorFork count={layout.centerDescendants.length} />
                <div className="flex items-start gap-4">
                  {layout.centerDescendants.map((descendant) => (
                    <DescendantColumn key={descendant.link.id} branch={descendant} t={t} />
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {layout.spouses.map((link) => (
            <div key={link.id} className="flex flex-col items-center gap-2">
              <PersonCard
                person={link.related}
                relationshipLabel={formatLinkRelationship(link, t)}
              />
            </div>
          ))}
        </div>

        {layout.unplaced.length > 0 ? (
          <div className="flex flex-wrap items-start justify-center gap-3 border-t border-border pt-6">
            {layout.unplaced.map((link) => (
              <PersonCard
                key={link.id}
                person={link.related}
                relationshipLabel={formatLinkRelationship(link, t)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const FamilyGridView = ({
  centerPerson,
  tree,
}: {
  centerPerson: FamilyPerson;
  tree: FamilyTreeData;
}) => {
  const { t } = useTranslation('people');
  const rows = buildHierarchicalListRows(centerPerson, tree);

  return (
    <div className="space-y-2 rounded-lg border border-border bg-surface-sunken/40 p-4">
      {rows.map((row) => {
        const name = row.isCenter
          ? displayPersonName(centerPerson)
          : displayPersonName(row.link.related);
        const personId = row.isCenter ? centerPerson.id : row.link.related.id;

        return (
          <div
            key={row.link.id}
            className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
            style={{ marginInlineStart: `${row.depth * 1.75}rem` }}
          >
            <MemberPhoto
              src={row.isCenter ? centerPerson.photoUrl : row.link.related.photoUrl}
              name={name}
              size="sm"
            />
            <div className="min-w-0 flex-1">
              <MemberNameLink memberId={personId} name={name} className="font-medium" />
              <p className="text-caption text-fg-secondary">
                {row.isCenter
                  ? t('families.tree.selectedPerson')
                  : formatLinkRelationship(row.link, t)}
                {row.anchorName ? ` · ${t('families.list.under', { name: row.anchorName })}` : ''}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const FamilyListView = ({
  centerPerson,
  tree,
}: {
  centerPerson: FamilyPerson;
  tree: FamilyTreeData;
}) => {
  const { t } = useTranslation('people');
  const dash = '—';
  const rows = buildHierarchicalListRows(centerPerson, tree);

  type FamilyListRow = (typeof rows)[number];

  const columns: DataTableColumn<FamilyListRow>[] = [
    {
      id: 'name',
      header: t('members.family.columns.name'),
      cell: (row) => (
        <span style={{ paddingInlineStart: `${row.depth * 1.25}rem` }}>
          <MemberNameLink
            memberId={row.isCenter ? centerPerson.id : row.link.related.id}
            name={
              row.isCenter
                ? displayPersonName(centerPerson)
                : displayPersonName(row.link.related)
            }
            className="font-medium"
          />
        </span>
      ),
    },
    {
      id: 'under',
      header: t('families.list.underColumn'),
      cell: (row) => row.anchorName ?? dash,
    },
    {
      id: 'relationship',
      header: t('members.family.columns.relationship'),
      cell: (row) =>
        row.isCenter ? t('families.tree.selectedPerson') : formatLinkRelationship(row.link, t),
    },
    {
      id: 'gender',
      header: t('members.family.columns.gender'),
      cell: (row) => {
        const gender = row.isCenter ? centerPerson.gender : row.link.related.gender;
        return gender ? t(`members.gender.${gender}`) : dash;
      },
    },
    {
      id: 'notes',
      header: t('members.family.columns.notes'),
      cell: (row) => (row.isCenter ? dash : row.link.notes ?? dash),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.link.id}
    />
  );
};

export const FamilyTreeDisplay = ({ centerPerson, tree, view }: FamilyTreeDisplayProps) => {
  if (view === 'tree') {
    return <FamilyTreeView centerPerson={centerPerson} tree={tree} />;
  }
  if (view === 'grid') {
    return <FamilyGridView centerPerson={centerPerson} tree={tree} />;
  }
  return <FamilyListView centerPerson={centerPerson} tree={tree} />;
};
